import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, Loader2, ArrowLeft } from "lucide-react";
import { MobileFrame } from "../../components/MobileFrame";
import { useAuth, DEMO_ACCOUNTS } from "../../context/AuthContext";
import { ROLES, ROLE_LABELS } from "../../constants/config";

const DEMO_ROLES = [
  { key: ROLES.SALES, email: DEMO_ACCOUNTS.sales.email },
  { key: ROLES.TEAM_LEADER, email: DEMO_ACCOUNTS.team_leader.email },
  { key: ROLES.KEPALA_CABANG, email: DEMO_ACCOUNTS.kepala_cabang.email },
  { key: ROLES.MAIN_DEALER, email: DEMO_ACCOUNTS.main_dealer.email },
];

const roleToHomeRoute = (role) => {
  if (role === ROLES.SALES) return "/sales/dashboard";
  return "/admin/dashboard";
};

export default function Login() {
  const navigate = useNavigate();
  const { login, loginAsDemo } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    navigate(roleToHomeRoute(res.user.role));
  };

  const handleDemo = async (role) => {
    setError("");
    setLoading(true);
    const res = await loginAsDemo(role);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    navigate(roleToHomeRoute(res.user.role));
  };

  return (
    <MobileFrame>
      <div className="min-h-full bg-honda-bg flex flex-col">
        {/* Header */}
        <div className="px-6 pt-12 pb-6 bg-white border-b border-honda-border">
          <button
            data-testid="login-back-button"
            onClick={() => navigate("/")}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-honda-bg transition-colors -ml-2"
          >
            <ArrowLeft size={20} className="text-honda-ink" />
          </button>
          <div className="mt-4 flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-honda-red text-white flex items-center justify-center font-extrabold text-lg font-heading">
              H
            </div>
            <span className="font-heading font-bold text-honda-ink text-lg">SALES TRACK</span>
          </div>
          <h1 className="mt-5 font-heading font-extrabold text-3xl text-honda-ink leading-tight">
            Masuk ke Akun
          </h1>
          <p className="mt-1 text-sm text-honda-muted">
            Gunakan akun yang diberikan Dealer Admin Anda.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin} className="px-6 py-6 space-y-4">
          <div>
            <label className="honda-label">Email</label>
            <div className="relative mt-1.5">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-honda-subtle" />
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nama@hondababel.id"
                className="honda-input pl-9"
                required
              />
            </div>
          </div>
          <div>
            <label className="honda-label">Password</label>
            <div className="relative mt-1.5">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-honda-subtle" />
              <input
                data-testid="login-password-input"
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="honda-input pl-9 pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-honda-subtle hover:text-honda-ink"
                data-testid="login-toggle-password-visibility"
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && (
            <div
              data-testid="login-error-message"
              className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2"
            >
              {error}
            </div>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full honda-btn-primary py-3.5 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} /> Masuk...
              </>
            ) : (
              "Masuk"
            )}
          </button>

          <div className="text-right">
            <button
              type="button"
              data-testid="login-forgot-password"
              className="text-xs text-honda-red font-medium hover:underline"
            >
              Lupa password?
            </button>
          </div>
        </form>

        {/* Demo accounts */}
        <div className="mt-2 px-6 pb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 h-px bg-honda-border" />
            <span className="text-[11px] uppercase tracking-wider text-honda-subtle font-semibold">
              Demo Login
            </span>
            <div className="flex-1 h-px bg-honda-border" />
          </div>
          <p className="text-xs text-honda-muted mb-3 text-center">
            Klik salah satu role untuk login cepat dengan data demo.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {DEMO_ROLES.map((r) => (
              <button
                key={r.key}
                data-testid={`demo-login-${r.key}`}
                onClick={() => handleDemo(r.key)}
                className="text-left p-3 rounded-xl border border-honda-border bg-white hover:border-honda-red hover:bg-honda-red-light transition-colors"
              >
                <p className="text-[11px] uppercase tracking-wider font-semibold text-honda-red">
                  {ROLE_LABELS[r.key]}
                </p>
                <p className="text-xs text-honda-muted mt-0.5 truncate">{r.email}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </MobileFrame>
  );
}
